import { useMemo } from 'react';
import { Card } from '@/components/shadcn/card';
import type { PadelSessionListItem } from '@/services/padelService';

interface PadelTeamRecapProps {
  sessions: PadelSessionListItem[];
  teamAName: string;
  teamBName: string;
}

interface TeamSummary {
  teamAPoints: number;
  teamBPoints: number;
  teamAWins: number;
  teamBWins: number;
  winner: 'A' | 'B' | 'DRAW';
}

function summarize(sessions: PadelSessionListItem[]): TeamSummary {
  let teamAPoints = 0;
  let teamBPoints = 0;
  let teamAWins = 0;
  let teamBWins = 0;

  sessions
    .flatMap((session) => session.games ?? [])
    .forEach((game) => {
      const scoreA = game.scoreSideA ?? 0;
      const scoreB = game.scoreSideB ?? 0;
      if (game.status !== 'completed' && scoreA === 0 && scoreB === 0) return;
      teamAPoints += scoreA;
      teamBPoints += scoreB;
      const sideAWon =
        scoreA > scoreB || game.winnerSide === 'a' || game.winnerSide === 'side_a';
      const sideBWon =
        scoreB > scoreA || game.winnerSide === 'b' || game.winnerSide === 'side_b';
      if (sideAWon) teamAWins += 1;
      else if (sideBWon) teamBWins += 1;
    });

  let winner: TeamSummary['winner'] = 'DRAW';
  if (teamAWins > teamBWins || (teamAWins === teamBWins && teamAPoints > teamBPoints)) winner = 'A';
  else if (teamBWins > teamAWins || (teamAWins === teamBWins && teamBPoints > teamAPoints)) {
    winner = 'B';
  }

  return { teamAPoints, teamBPoints, teamAWins, teamBWins, winner };
}

export default function PadelTeamRecap({ sessions, teamAName, teamBName }: PadelTeamRecapProps) {
  const summary = useMemo(() => summarize(sessions), [sessions]);

  const side = (name: string, wins: number, points: number, won: boolean, align: 'start' | 'end') => (
    <div className={align === 'end' ? 'text-right' : 'text-left'}>
      <p className="truncate text-sm font-extrabold text-[#0E1726]">{name}</p>
      <p className="mt-1 text-2xl font-extrabold text-[#0E1726]">
        {wins}
        <span className="ml-1 text-xs font-semibold text-gray-400">menang</span>
      </p>
      <p className="text-[11px] text-gray-400">{points} total poin</p>
      {won && <p className="mt-1 text-[10px] font-bold text-emerald-600">MENANG</p>}
    </div>
  );

  return (
    <Card className="rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Rekap Tim</p>
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        {side(teamAName, summary.teamAWins, summary.teamAPoints, summary.winner === 'A', 'end')}
        <span className="text-[10px] font-extrabold tracking-widest text-gray-400">VS</span>
        {side(teamBName, summary.teamBWins, summary.teamBPoints, summary.winner === 'B', 'start')}
      </div>
    </Card>
  );
}
